import each from "lodash/each";
import trim from "lodash/trim";
import { Breakpoint } from "../utils/Breakpoints";
import parseQueryString from "../utils/ParseQueryString";
import TagManager from "react-gtm-module";

type carGroup = {
  groupEl: HTMLDivElement;
  itemEls: HTMLAllCollection;
  max: number;
  current: number;
  pips: HTMLUListElement;
};

enum carNavigate {
  NONE = "NONE",
  PREV = "PREV",
  NEXT = "NEXT",
}

class CarSelect {
  private classNameRoot = "carSelect";
  scope: Element = null;
  private isMultiSelect: boolean = false;
  autoSelected: boolean = false;
  private carGroups: carGroup[] = [];
  private touches: any = {
    startX: 0,
    endX: 0,
  };
  selectedCars: any[] = [];
  originalParams: string;

  private immedate: boolean = false;

  constructor(multiSelect: boolean = false, immediateSelect: boolean = false) {
    this.scope = document.querySelector(`.${this.classNameRoot}`);
    if (!this.scope) return;

    this.immedate = immediateSelect;
    this.isMultiSelect = multiSelect;
    this.parseOriginalParams();
    this.prepareNav();
    this.attachEvents();
    this.autoSelect();
  }

  private parseOriginalParams() {
    const queryStr = parseQueryString();
    let newQueryStr = "";
    Object.keys(queryStr).map(function (keyName, keyIndex) {
      if (keyName !== "model") {
        newQueryStr += `&${keyName}=${queryStr[keyName]}`;
      }
    });
    this.originalParams = newQueryStr;
  }

  private prepareNav(): void {
    const groups = this.scope.querySelectorAll(`.${this.classNameRoot}__group`);
    each(groups, (group, index) => {
      const items = group.querySelectorAll(`.${this.classNameRoot}__item`);
      const pips = document.createElement("ul");
      pips.className = "pips";
      for (let i = 0; i < items.length; i++) {
        const pip = document.createElement("li");
        pip.className = "pips__item";
        pip.addEventListener("click", () => {
          this.navigateByPip(i, index);
        });
        pips.appendChild(pip);
      }
      group.appendChild(pips);

      this.carGroups[index] = {
        groupEl: group,
        itemEls: items,
        max: items.length,
        current: 0,
        pips,
      };

      this.navigateItem(this.carGroups[index], carNavigate.NONE);
    });
  }

  private attachEvents(): void {
    //Car Clicks
    each(
      this.scope.querySelectorAll(`.${this.classNameRoot}__item`),
      (carItem: Element): void => {
        carItem.addEventListener("click", () =>
          this.selectItem(carItem, this.immedate)
        );
      }
    );

    this.carGroups.forEach((group: carGroup) => {
      const prev = group.groupEl.querySelector(".carSelect__navControl--prev");
      const next = group.groupEl.querySelector(".carSelect__navControl--next");

      group.groupEl.addEventListener("touchstart", (e) =>
        this.handleTouchStart(e, group)
      );
      group.groupEl.addEventListener("touchend", (e) =>
        this.handleTouchEnd(e, group)
      );

      next &&
        next.addEventListener("click", (e: Event) =>
          this.navigateItem(group, carNavigate.NEXT)
        );
      prev &&
        prev.addEventListener("click", (e: Event) =>
          this.navigateItem(group, carNavigate.PREV)
        );
    });
  }

  private navigateByPip(pipNumber: number, groupIndex: number): void {
    const group = this.carGroups[groupIndex];
    if (pipNumber === group.current) return;

    const diff = pipNumber - group.current;
    if (!diff) return;

    const cmp = diff < 0 ? diff * -1 : diff;

    for (let i = 0; i < cmp; i++) {
      this.navigateItem(group, diff > 0 ? carNavigate.NEXT : carNavigate.PREV);
    }
  }

  private navigateItem(
    group: carGroup,
    direction: carNavigate = carNavigate.NEXT
  ): void {
    const bp = Breakpoint();
    const isMob = bp === "xxs" || bp === "xs";
    const isDesktop = bp === "md" || bp === "lg";
    const navClasses = ["carSelect__navControls"];

    switch (direction) {
      case carNavigate.PREV:
        if (group.current === 0) return;
        group.current--;
        break;

      case carNavigate.NEXT:
        if (isMob && group.current >= group.max - 1) return;
        if (!isMob && group.current + 2 >= group.max - 1) return;
        group.current++;
        break;

      case carNavigate.NONE:
        //Nothing, just update visibility & pips
        break;
    }

    //Update Visibiilty
    const currentItems = [group.current];
    if (!isMob) {
      currentItems.push(group.current + 1);
      currentItems.push(group.current + 2);
    }

    each(group.itemEls, (car: HTMLDivElement, index: number) => {
      if (!isDesktop) {
        car.style.transform = `translateX(-${group.current * 100}%)`;
      } else {
        car.style.transform = ""; // remove slider displacement
      }

      car.classList.toggle(
        "active",
        !!currentItems.filter((item) => item === index).length
      );
    });

    //Update Nav Visibilty
    const nav = group.groupEl.querySelector(".carSelect__navControls");
    if (nav) {
      group.current === 0 && navClasses.push("hide-prev");
      isMob && group.current >= group.max - 1 && navClasses.push("hide-next");
      !isMob &&
        group.current + 2 >= group.max - 1 &&
        navClasses.push("hide-next");
      nav.className = navClasses.join(" ");
    }

    //Update Pips
    const pips = group.pips.querySelectorAll(`.pips__item`);

    each(group.itemEls, (car: HTMLDivElement, index: number) => {
      pips[index].classList.toggle(
        "pips__item--active",
        index === group.current
      );
    });
  }

  private updateUrl() {
    history.replaceState(
      null,
      null,
      this.selectedCars.length
        ? `?model=${this.selectedCars
            .map((item) => {
              const name = (item.modelName || "").toLowerCase();
              if (name === "all-new outback") return "outback2026";
              if (name === "all-new outback wilderness")
                return "wilderness2026";
              return name;
            })
            .join(",")}${this.originalParams}`
        : `?${this.originalParams}`
    );
  }

  isBookTestPage() {
    return document.location.pathname === "/book-test-drive";
  }

  selectItem(
    carItem,
    immediate: boolean = false,
    triggerReload: boolean = false,
    callback: Function = function () {}
  ): void {
    const modelName: string = carItem.getAttribute("data-modelName");
    if (!modelName) return;

    const isSelected: boolean =
      this.selectedCars.filter((i) => i.modelName === modelName).length > 0;

    if (!this.isMultiSelect && this.selectedCars.length) return;

    if (isSelected) {
      return this.deselectItem(carItem, modelName);
    }
    if (this.selectedCars.length >= 3) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "model_selected",
        selected_model: modelName,
      },
    });

    this.selectedCars.push({ modelName, carItem });

    carItem.classList.add(`${this.classNameRoot}__item--selected`);
    this.scope.classList.add(`${this.classNameRoot}--selected`);

    this.updateUrl();
    triggerReload && location.reload();

    setTimeout(
      () => this.onSelectionComplete(this.selectedCars),
      immediate ? 0 : 1500
    );
  }

  deselectItem(carItem, modelName): void {
    let index = -1;

    this.selectedCars.forEach((c, i) => {
      if (c.modelName === modelName) index = i;
    });

    if (index !== -1) {
      this.selectedCars.splice(index, 1);
    }

    carItem.classList.remove(`${this.classNameRoot}__item--selected`);

    if (this.selectedCars.length < 1) {
      this.scope.classList.remove(`${this.classNameRoot}--selected`);
    }
    this.updateUrl();
    setTimeout(() => this.onDeselectionComplete(this.selectedCars), 0);

    TagManager.dataLayer({
      dataLayer: {
        event: "model_deselected",
        selected_model: modelName,
      },
    });
  }

  private autoSelect(): void {
    const query = parseQueryString();
    let selected: number = 0;
    let modelsQuery =
      typeof query.model === "undefined"
        ? ""
        : decodeURI(query.model.toLowerCase());

    let models = modelsQuery
      .replace("wrx or wrx sti", "wrx,wrx sti")
      .replace("wrx and wrx sti", "wrx,wrx sti")
      .split(",")
      .map((item) => trim(item))
      .filter((item) => !!item);

    if (models.length < 1) return;

    if (models.length > 3) models = models.slice(0, 3);

    if (!this.isMultiSelect) models = [models[0]];

    const carItems = this.scope.querySelectorAll(
      `.${this.classNameRoot}__item`
    );
    each(carItems, (carItem) => {
      const model = carItem.getAttribute("data-modelName");
      if (!model) return;
      const index = models.indexOf(model.toLowerCase());

      if (index < 0) return;
      selected++;
      this.selectItem(carItem, true, false);
    });

    if (selected) this.autoSelected = true;
  }

  private handleTouchStart(e, group) {
    this.touches.startX = e.changedTouches[0].screenX;
  }

  private handleTouchEnd(e, group) {
    this.touches.endX = e.changedTouches[0].screenX;
    const difference = this.touches.endX - this.touches.startX;

    if (difference > 100) this.navigateItem(group, carNavigate.PREV);
    if (difference < -100) this.navigateItem(group, carNavigate.NEXT);
  }

  onDeselectionComplete(selectedCars): void {}

  onSelectionComplete(selectedCars): void {}

  reset(): void {
    this.selectedCars = [];

    each(
      this.scope.querySelectorAll(`.${this.classNameRoot}__item`),
      (carItem: Element): void => {
        carItem.classList.remove(`${this.classNameRoot}__item--selected`);
      }
    );

    this.scope.classList.remove(`${this.classNameRoot}--selected`);
  }
}

export default CarSelect;
